import React from 'react';
import { Coupon } from '../types/Coupon';
import { MdLocalOffer, MdCheckCircle, MdCancel, MdTrendingUp } from 'react-icons/md';

interface DashboardProps {
  coupons: Coupon[];
}

export const Dashboard: React.FC<DashboardProps> = ({ coupons }) => {
  // 統計情報の計算
  const totalCoupons = coupons.length;
  const activeCoupons = coupons.filter(c => c.status === 'active').length;
  const expiredCoupons = coupons.filter(c => c.status === 'expired').length;
  const totalUsed = coupons.reduce((sum, c) => sum + c.usedCount, 0);
  
  // 最近のクーポン（作成日順）
  const recentCoupons = [...coupons]
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
    .slice(0, 5);

  // 人気のクーポン（使用回数順）
  const popularCoupons = [...coupons]
    .sort((a, b) => b.usedCount - a.usedCount)
    .slice(0, 5);

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'active': return '有効';
      case 'inactive': return '無効';
      case 'expired': return '期限切れ';
      case 'draft': return '下書き';
      default: return status;
    }
  };

  const getStatusClass = (status: string) => {
    switch(status) {
      case 'active': return 'status-active';
      case 'inactive': return 'status-inactive';
      case 'expired': return 'status-expired';
      case 'draft': return 'status-draft';
      default: return '';
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatDiscount = (coupon: Coupon) => {
    if (coupon.discountType === 'percentage') {
      return `${coupon.discountValue}%割引`;
    } else {
      return `¥${coupon.discountValue.toLocaleString()}割引`;
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h2>ダッシュボード</h2>
      </div>

      {/* 統計カード */}
      <div className="stats-grid">
        <div className="stat-card stat-card-primary">
          <div className="stat-icon">
            {React.createElement(MdLocalOffer as any)}
          </div>
          <div className="stat-content">
            <div className="stat-label">総クーポン数</div>
            <div className="stat-value">{totalCoupons}</div>
          </div>
        </div>

        <div className="stat-card stat-card-success">
          <div className="stat-icon">
            {React.createElement(MdCheckCircle as any)}
          </div>
          <div className="stat-content">
            <div className="stat-label">有効なクーポン</div>
            <div className="stat-value">{activeCoupons}</div>
          </div>
        </div>

        <div className="stat-card stat-card-danger">
          <div className="stat-icon">
            {React.createElement(MdCancel as any)}
          </div>
          <div className="stat-content">
            <div className="stat-label">期限切れ</div>
            <div className="stat-value">{expiredCoupons}</div>
          </div>
        </div>

        <div className="stat-card stat-card-info">
          <div className="stat-icon">
            {React.createElement(MdTrendingUp as any)}
          </div>
          <div className="stat-content">
            <div className="stat-label">総使用回数</div>
            <div className="stat-value">{totalUsed}</div>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        {/* 最近のクーポン */}
        <div className="dashboard-section">
          <div className="section-header">
            <h3>最近作成されたクーポン</h3>
          </div>
          <div className="coupon-list-table">
            {recentCoupons.length === 0 ? (
              <div className="empty-state">
                <p>クーポンがありません</p>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>クーポン名</th>
                    <th>コード</th>
                    <th>割引内容</th>
                    <th>有効期限</th>
                    <th>ステータス</th>
                    <th>使用回数</th>
                  </tr>
                </thead>
                <tbody>
                  {recentCoupons.map(coupon => (
                    <tr key={coupon.id}>
                      <td className="coupon-name">{coupon.name}</td>
                      <td className="coupon-code">{coupon.code}</td>
                      <td>{formatDiscount(coupon)}</td>
                      <td className="date-cell">
                        {formatDate(coupon.startDate)} 〜 {formatDate(coupon.endDate)}
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusClass(coupon.status)}`}>
                          {getStatusLabel(coupon.status)}
                        </span>
                      </td>
                      <td className="usage-cell">
                        {coupon.usedCount}
                        {coupon.usageLimit && ` / ${coupon.usageLimit}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* 人気のクーポン */}
        <div className="dashboard-section">
          <div className="section-header">
            <h3>人気のクーポン（使用回数順）</h3>
          </div>
          <div className="coupon-list-table">
            {popularCoupons.length === 0 ? (
              <div className="empty-state">
                <p>クーポンがありません</p>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>クーポン名</th>
                    <th>コード</th>
                    <th>割引内容</th>
                    <th>使用回数</th>
                    <th>ステータス</th>
                  </tr>
                </thead>
                <tbody>
                  {popularCoupons.map(coupon => (
                    <tr key={coupon.id}>
                      <td className="coupon-name">{coupon.name}</td>
                      <td className="coupon-code">{coupon.code}</td>
                      <td>{formatDiscount(coupon)}</td>
                      <td className="usage-cell">
                        <strong>{coupon.usedCount}</strong>
                        {coupon.usageLimit && ` / ${coupon.usageLimit}`}
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusClass(coupon.status)}`}>
                          {getStatusLabel(coupon.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

